/**
 * Report tab export layout aligned with ReportCards.jsx (card blocks, blank lines between cards).
 */
import {
  formatNumber,
  formatDifference,
  formatRank,
  formatPercentile,
} from './formatting';
import { getCrimeDisplayName } from '../constants/crimes';
import { getStateAbbreviation } from '../constants/locations';

function isFiniteNum(v) {
  if (v === null || v === undefined || v === '' || v === 'N/A') return false;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return !Number.isNaN(n);
}

function getReportCurrentData(ctx) {
  const { view, locationData, locationTypeData, selectedCrime } = ctx;
  if (view === 'locationType') {
    const ca = locationTypeData?.data?.crimeAggregates;
    if (ca && ca[selectedCrime]) return ca[selectedCrime];
    return locationTypeData?.data?.aggregateStats || {};
  }
  return locationData?.data?.current || {};
}

function hasAggregateStructure(c) {
  return !!(c?.totals && c?.trends && c?.rates && c?.scores);
}

function reportGetRateComparisonValue(data, crime, rateType, rateScope) {
  if (!data) return 0;
  if (rateType === 'county') {
    return rateScope === 'state'
      ? data[`${crime}_all_counties_state_rate`]
      : data[`${crime}_all_counties_national_rate`];
  }
  if (rateType === 'city') {
    return rateScope === 'state'
      ? data[`${crime}_all_cities_state_rate`]
      : data[`${crime}_all_cities_national_rate`];
  }
  if (rateType === 'metro') {
    return data[`${crime}_all_metros_rate`];
  }
  return rateScope === 'state'
    ? data[`${crime}_state_rate`]
    : data[`${crime}_national_rate`];
}

function reportGetRateDifferenceValue(data, crime, rateType, rateScope) {
  if (!data) return 0;
  if (rateType === 'county') {
    return rateScope === 'state'
      ? data[`${crime}_all_counties_state_rate_diff`]
      : data[`${crime}_all_counties_national_rate_diff`];
  }
  if (rateType === 'city') {
    return rateScope === 'state'
      ? data[`${crime}_all_cities_state_rate_diff`]
      : data[`${crime}_all_cities_national_rate_diff`];
  }
  if (rateType === 'metro') {
    return data[`${crime}_all_metros_rate_diff`];
  }
  return rateScope === 'state'
    ? data[`${crime}_state_rate_diff`]
    : data[`${crime}_national_rate_diff`];
}

function reportGetRatePercentDiffValue(data, crime, rateType, rateScope) {
  if (!data) return 0;
  if (rateType === 'county') {
    return rateScope === 'state'
      ? data[`${crime}_all_counties_state_rate_diff_pct`]
      : data[`${crime}_all_counties_national_rate_diff_pct`];
  }
  if (rateType === 'city') {
    return rateScope === 'state'
      ? data[`${crime}_all_cities_state_rate_diff_pct`]
      : data[`${crime}_all_cities_national_rate_diff_pct`];
  }
  if (rateType === 'metro') {
    return data[`${crime}_all_metros_rate_diff_pct`];
  }
  return rateScope === 'state'
    ? data[`${crime}_state_rate_diff_pct`]
    : data[`${crime}_national_rate_diff_pct`];
}

function getRateComparisonLabel(rateType, rateScope, prefixes, crimeName, isCounty, isCity) {
  if (rateType === 'county') {
    return rateScope === 'state'
      ? `${prefixes.state} County ${crimeName} Rate`
      : `US County ${crimeName} Rate`;
  }
  if (rateType === 'city') {
    return rateScope === 'state'
      ? `${prefixes.state} City ${crimeName} Rate`
      : `US City ${crimeName} Rate`;
  }
  if (rateType === 'metro') {
    return `Metro ${crimeName} Rate`;
  }
  return rateScope === 'state'
    ? `${prefixes.state} State ${crimeName} Rate`
    : `${isCounty || isCity ? 'US' : ''} National ${crimeName} Rate`.replace(/^\s+/, '');
}

function getDifferenceLabel(rateType, rateScope, prefixes, isCounty, isCity) {
  if (rateType === 'county') {
    return rateScope === 'state'
      ? `${prefixes.state} County Rate Difference`
      : `US County Rate Difference`;
  }
  if (rateType === 'city') {
    return rateScope === 'state'
      ? `${prefixes.state} City Rate Difference`
      : `US City Rate Difference`;
  }
  if (rateType === 'metro') {
    return `Metro Rate Difference`;
  }
  return rateScope === 'state'
    ? `${prefixes.state} State Rate Difference`
    : `${isCounty || isCity ? 'US' : ''} National Rate Difference`.replace(/^\s+/, '');
}

function formatRateSecondaryExport(diffValue, percentValue) {
  if (
    !isFiniteNum(diffValue) ||
    !isFiniteNum(percentValue)
  ) {
    return '-';
  }
  return `${formatDifference(diffValue, false, true)} (${formatDifference(percentValue, true)})`;
}

/**
 * @returns {{ headers: ['Metric', 'Value'], rows: [string, string][] }} Two columns; blank rows separate cards.
 */
export function buildReportCardsExportRows(ctx, geo) {
  const {
    locType,
    isNation,
    isCountyOrCity,
    isMetro,
    stateAbbr: stateAbbrParam,
    compareMode,
    rateViewMode,
    view,
  } = geo;

  const { locationData, selectedCrime, selectedSource } = ctx;
  const cp = selectedCrime.toLowerCase();
  const current = getReportCurrentData(ctx);
  const displayName = getCrimeDisplayName(selectedCrime, selectedSource);

  const info = locationData?.info || {};
  const name = info.name || '';
  const isCounty = locType === 'county';
  const isCity = locType === 'city';
  const locationPrefix = locType.charAt(0).toUpperCase() + locType.slice(1);

  const isLocationTypeView = view === 'locationType' || name.startsWith('All ');
  const isCountyTypeView = isLocationTypeView && name.includes('Counties');
  const isCityTypeView = isLocationTypeView && name.includes('Cities');
  const stateFromAllCounties = isCountyTypeView && name.match(/All\s+([A-Z]{2})\s+Counties/);
  const stateFromAllCities = isCityTypeView && name.match(/All\s+([A-Z]{2})\s+Cities/);
  const stateAbbrFromAll = stateFromAllCounties ? stateFromAllCounties[1] : stateFromAllCities ? stateFromAllCities[1] : '';

  const isHigherGeography = ['region', 'division', 'state', 'metro', 'county', 'city'].includes(info.type);
  const hideDetails = isHigherGeography && isLocationTypeView;

  let countyStateAbbr = '';
  let cityStateAbbr = '';
  if ((isCounty || isCity) && name) {
    const stateName = name.split(',')[1]?.trim();
    if (isCounty) countyStateAbbr = getStateAbbreviation(stateName);
    else if (isCity) cityStateAbbr = getStateAbbreviation(stateName);
  }

  const comparisonPrefix = {
    state:
      isCountyTypeView && stateAbbrFromAll
        ? stateAbbrFromAll
        : isCityTypeView && stateAbbrFromAll
          ? stateAbbrFromAll
          : countyStateAbbr || cityStateAbbr || stateAbbrFromAll || stateAbbrParam,
    national: 'US',
  };

  const cm = compareMode || 'state';

  const usesPeerRateFields = isCounty || isCity || isMetro;
  const safeRm = rateViewMode || {};
  const effectiveRateViewMode = usesPeerRateFields
    ? {
        rateType:
          safeRm.rateType ||
          (isCounty ? 'county' : isCity ? 'city' : 'metro'),
        rateScope: safeRm.rateScope || cm || 'state',
      }
    : { rateType: 'state', rateScope: 'national' };

  const rows = [];
  const row = (metric, value) => {
    rows.push([String(metric ?? ''), value == null ? '' : String(value)]);
  };
  const cardEnd = () => rows.push(['', '']);

  const agg = hasAggregateStructure(current);

  // ─── Card: Totals ─────────────────────────────────────────────────────
  const totalPrimary = agg
    ? formatNumber(current.totals.total)
    : formatNumber(current[`${cp}_count`]);
  row('KRIME Total', totalPrimary);

  if (!isNation) {
    let avgLabel;
    if (isCounty || isCity) {
      avgLabel = `${comparisonPrefix[cm]} ${locationPrefix} ${displayName} Average`;
    } else {
      avgLabel = `${locationPrefix} ${displayName} Average`;
    }
    let avgVal;
    if (agg) {
      if ((isCountyTypeView || isCityTypeView) && cm === 'state') {
        avgVal = formatNumber(current.totals.stateAverage);
      } else if ((isCountyTypeView || isCityTypeView) && cm === 'national') {
        avgVal = formatNumber(
          current.totals.average || current[`${cp}_national_count_avg`],
        );
      } else {
        avgVal = formatNumber(current.totals.average);
      }
    } else if (isCountyOrCity && cm === 'state') {
      avgVal = formatNumber(current[`${cp}_state_count_avg`]);
    } else {
      avgVal = formatNumber(current[`${cp}_national_count_avg`]);
    }
    row(avgLabel, avgVal);

    let pctLabel;
    if (isLocationTypeView && !isCountyTypeView && !isCityTypeView) {
      pctLabel = `Total ${locationPrefix}s`;
    } else if ((isCountyTypeView || isCityTypeView) && cm === 'state') {
      pctLabel = `Total ${stateAbbrFromAll} ${isCountyTypeView ? 'Counties' : 'Cities'}`;
    } else if ((isCountyTypeView || isCityTypeView) && cm === 'national') {
      pctLabel = `Total US ${isCountyTypeView ? 'Counties' : 'Cities'}`;
    } else if (isCounty || isCity) {
      pctLabel = `${comparisonPrefix[cm]} ${locationPrefix} Percentile`;
    } else {
      pctLabel = `${locationPrefix} Percentile`;
    }

    let pctVal;
    if (agg) {
      if ((isCountyTypeView || isCityTypeView) && cm === 'state') {
        pctVal = formatNumber(current.totals.stateLocations);
      } else if ((isCountyTypeView || isCityTypeView) && cm === 'national') {
        pctVal = formatNumber(current.totals.nationalLocations);
      } else {
        pctVal = formatNumber(current.totals.locations);
      }
    } else if (isCountyOrCity && cm === 'state') {
      pctVal = formatPercentile(current[`${cp}_state_count_percentile`]);
    } else if (isCountyOrCity && cm === 'national') {
      pctVal = formatPercentile(current[`${cp}_national_count_percentile`]);
    } else {
      pctVal = formatPercentile(current[`${cp}_count_percentile`]);
    }
    row(pctLabel, pctVal);
  }
  cardEnd();

  // ─── Card: Trend ───────────────────────────────────────────────────────
  const trendPrimary = agg
    ? formatDifference(current.trends.percentChange, true)
    : formatDifference(current[`${cp}_count_prev_diff_pct`], true);
  row('KRIME Trend', trendPrimary);
  if (!isNation) {
    const prevTotal = agg
      ? formatNumber(current.trends.previous)
      : formatNumber(current[`${cp}_count_prev`]);
    row(`Previous ${displayName} Total`, prevTotal);
    const chg = agg
      ? formatDifference(current.trends.difference)
      : formatDifference(current[`${cp}_count_prev_diff`]);
    row('Change from Previous', chg);
  }
  cardEnd();

  // ─── Card: Rate ────────────────────────────────────────────────────────
  const ratePrimary = agg
    ? formatNumber(current.rates.rate)
    : formatNumber(current[`${cp}_rate`]);
  row('KRIME Rate', ratePrimary);
  if (!isNation && !hideDetails) {
    const rcLabel = getRateComparisonLabel(
      effectiveRateViewMode.rateType,
      effectiveRateViewMode.rateScope,
      comparisonPrefix,
      displayName,
      isCounty,
      isCity,
    );
    const rcVal = agg
      ? formatNumber(current.rates.nationalRate)
      : formatNumber(
          reportGetRateComparisonValue(
            current,
            cp,
            effectiveRateViewMode.rateType,
            effectiveRateViewMode.rateScope,
          ),
        );
    row(rcLabel, rcVal);
    const diffLabel = getDifferenceLabel(
      effectiveRateViewMode.rateType,
      effectiveRateViewMode.rateScope,
      comparisonPrefix,
      isCounty,
      isCity,
    );
    const diffVal = agg
      ? formatRateSecondaryExport(current.rates.difference, current.rates.percentDiff)
      : formatRateSecondaryExport(
          reportGetRateDifferenceValue(
            current,
            cp,
            effectiveRateViewMode.rateType,
            effectiveRateViewMode.rateScope,
          ),
          reportGetRatePercentDiffValue(
            current,
            cp,
            effectiveRateViewMode.rateType,
            effectiveRateViewMode.rateScope,
          ),
        );
    row(diffLabel, diffVal);
  }
  cardEnd();

  // ─── Card: Scores ──────────────────────────────────────────────────────
  const scoreTitle = isCountyOrCity
    ? cm === 'state'
      ? `${comparisonPrefix.state} KRIME Score™`
      : 'US KRIME Score™'
    : 'KRIME Score™';
  let scoreVal;
  if (isNation || hideDetails) {
    scoreVal = '-';
  } else if (agg) {
    scoreVal = formatNumber(current.scores.compositeScore, true);
  } else if (isCountyOrCity && cm === 'state') {
    scoreVal = formatNumber(current.state_composite_score, true);
  } else if (isCountyOrCity && cm === 'national') {
    scoreVal = formatNumber(current.national_composite_score, true);
  } else {
    scoreVal = formatNumber(current.composite_score, true);
  }
  row(scoreTitle, scoreVal);

  if (!isNation && !hideDetails) {
    const rankLabel = isCountyOrCity
      ? `${comparisonPrefix[cm]} ${locationPrefix} Rank (Composite)`
      : `${locationPrefix} Rank (Composite)`;
    let rankVal;
    if (agg) {
      rankVal = '-';
    } else if (isCountyOrCity && cm === 'state') {
      const rank = current.state_composite_score_rank;
      const tot = current.total_counties_state || current.total_cities_state;
      rankVal =
        rank && rank !== '' && rank !== 'N/A'
          ? `${formatRank(rank)} out of ${formatNumber(tot) || '-'}`
          : '-';
    } else if (isCountyOrCity && cm === 'national') {
      const rank = current.national_composite_score_rank;
      const tot = current.total_counties_national || current.total_cities_national;
      rankVal =
        rank && rank !== '' && rank !== 'N/A'
          ? `${formatRank(rank)} out of ${formatNumber(tot) || '-'}`
          : '-';
    } else {
      const rank = current.composite_score_rank;
      rankVal =
        rank && rank !== '' && rank !== 'N/A'
          ? `${formatRank(rank)} out of ${formatNumber(current.total_locations) || '-'}`
          : '-';
    }
    row(rankLabel, rankVal);
  }

  while (
    rows.length &&
    rows[rows.length - 1].length === 2 &&
    rows[rows.length - 1][0] === '' &&
    rows[rows.length - 1][1] === ''
  ) {
    rows.pop();
  }

  return { headers: ['Metric', 'Value'], rows };
}
